import axios from 'axios';
import { BASE_URL } from './helpers';
import { MediaType } from './structs';
import { MediaOutputSpec } from './structs/media-spec';
import { isRecord, toNumberValue, toStringValue } from './typeHelpers';

// ==================== Request Types ====================

interface NewMediaItemRequest {
  clip_id: string;
  type: MediaType;
  prompt: string;
  generator?: string;
  metadata?: Record<string, any>;
  output_spec?: MediaOutputSpec;
}

interface EditMediaItemRequest {
  new_prompt_string?: string;
  output_spec?: MediaOutputSpec;
  skip_regeneration?: boolean;
}

/**
 * Mirrors Go `models.MediaRef` (contentgen-go-backend internal/models/prompt.go:25-28).
 * The generator only ever consumes `url`; `media_id` is carried for provenance so
 * a reload can tell which media item a frame was bound to.
 */
export interface MediaRefPayload {
  media_id?: string;
  url?: string;
}

/**
 * Mirrors Go `models.FrameRef` (internal/models/prompt.go:30-33).
 * `frame` is the provider-facing slot name — "first" or "last".
 */
export interface FrameRefPayload {
  ref: MediaRefPayload;
  frame?: 'first' | 'last';
}

/**
 * Subset of Go `models.AIVideoSettings` (internal/models/prompt.go:48-61) that the
 * regenerate endpoint merges. Note the nesting: the backend's `MediaOutputSpec` is
 * NOT flat — reference frames live under `output_spec.ai_video.reference_frames`
 * and are merged by `mergeAIVideoSettings` (internal/api/media/regenerateMedia.go:229-231).
 */
export interface AIVideoSettingsPayload {
  reference_frames?: FrameRefPayload[];
  /**
   * Reference INPUTS — subject/style material the model carries into the shot,
   * as opposed to the anchor frames above. Flat lists of "media id or URL"
   * (Go `AIVideoSettings.ReferenceImages/Audios/Videos`), in wiring order; the
   * backend resolves an id to the item's current file, which is what keeps a
   * reference pointing at the right take after that still is regenerated.
   *
   * Merged by `mergeVideoReferenceLists` (internal/api/media/referenceInputs.go)
   * on the same present-beats-non-empty rule as `reference_frames`: a list that
   * is present replaces the stored one wholesale, empty list included, and an
   * absent key leaves the stored list alone.
   */
  reference_images?: string[];
  reference_audios?: string[];
  reference_videos?: string[];
}

export type RegenerateOutputSpec = MediaOutputSpec & {
  ai_video?: AIVideoSettingsPayload;
};

export interface RegenerateMediaRequest {
  output_spec?: RegenerateOutputSpec;
}

interface RenameMediaItemRequest {
  name: string;
  new_name?: string;
}

export interface MediaLibraryItem {
  id: string;
  media_id: string;
  type: string;
  name: string;
  url?: string;
  preview_url?: string;
  preview_video_url?: string;
  preview_audio_url?: string;
  thumbnail_url?: string;
  /**
   * A tiny (~24-32px) base64 webp data-URI for an instant blur-up placeholder.
   * Inlined in the list response so cells never flash empty while the real
   * thumbnail loads. Absent for legacy rows / non-images → graceful fallback.
   */
  micro_thumb?: string;
  poster_url?: string;
  playback_url?: string;
  mime_type?: string;
  source?: string;
  size_bytes?: number;
  clip_id?: string;
  pipeline_run_id?: string;
  pipeline_name?: string;
  is_favourite?: boolean;
  created_at?: string;
  prompt?: string;
  metadata?: Record<string, unknown>;
  /** Explicit durable-original evidence from the Store. Absent during rolling deploys. */
  has_file?: boolean;
  /**
   * The credits this generation cost, if the backend has settled it yet
   * (metadata.cost_credits). Absent on uploads/legacy rows and briefly on a
   * just-generated item before its async credit-settle completes.
   */
  costCredits?: number;
  status?: 'ready' | 'generating' | 'failed';
}

/**
 * Read the settled generation cost (in credits) from a media item's metadata.
 * Returns undefined when absent or non-finite so callers render gracefully
 * rather than showing NaN.
 */
export const readCostCredits = (metadata: unknown): number | undefined => {
  if (!isRecord(metadata)) return undefined;
  const raw = metadata.cost_credits;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};

export interface MediaLibraryListQuery {
  search?: string;
  type?: string;
  source?: string;
  role?: string;
  favourite?: boolean;
  sort?: 'newest' | 'oldest' | 'favourite' | 'name';
  created_after?: string;
  has_controlnet?: boolean;
  page?: number;
  limit?: number;
  /** Filters to test generations tied to a specific generator profile (GenerationsRail). */
  generator_profile_id?: string;
}

export interface MediaLibraryPage {
  items: MediaLibraryItem[];
  total: number;
  page: number;
  limit: number;
}

export interface CropImageRequest {
  /** Normalized coordinates in the source image (0..1). */
  x: number;
  y: number;
  width: number;
  height: number;
  name?: string;
}

/** Lineage of a media item — parent / co-variation siblings / variations. */
export interface RelatedMediaResult {
  parent: MediaLibraryItem | null;
  siblings: MediaLibraryItem[];
  variations: MediaLibraryItem[];
}

export interface MediaStats {
  total: number;
  image: number;
  video: number;
  audio: number;
}

interface HttpErrorWithStatus {
  response?: {
    status?: number;
  };
}


const normalizeMediaLibraryItem = (value: unknown, index: number): MediaLibraryItem | null => {
  if (!isRecord(value)) {
    if (typeof value === 'string') {
      return {
        id: value,
        media_id: value,
        type: 'unknown',
        name: value,
        url: value,
      };
    }
    return null;
  }

  const mediaUrl = toStringValue(
    value.url ??
      value.file_url ??
      value.asset_url ??
      value.uri ??
      value.media_url ??
      value.source_url ??
      value.download_url ??
      value.stream_url ??
      value.playback_url
  );
  const mediaId = toStringValue(value.media_id ?? value.id ?? value.asset_id, mediaUrl || `media-${index + 1}`);
  if (!mediaId) return null;

  const metadata = isRecord(value.metadata) ? value.metadata : undefined;
  const previewUrl = toStringValue(
    value.preview_url ??
      value.preview_image_url ??
      value.thumbnail_url ??
      value.thumb_url ??
      value.image_url
  );
  const previewVideoUrl = toStringValue(value.preview_video_url ?? value.video_url ?? value.video_file_url);
  const previewAudioUrl = toStringValue(value.preview_audio_url ?? value.audio_url ?? value.audio_file_url);
  const thumbnailUrl = toStringValue(value.thumbnail_url ?? value.thumb_url);
  const microThumb = toStringValue(value.micro_thumb ?? value.micro_thumb_data_uri ?? value.blur_data_uri);
  const posterUrl = toStringValue(value.poster_url ?? value.poster_image_url);
  const playbackUrl = toStringValue(value.playback_url ?? value.stream_url);

  return {
    id: mediaId,
    media_id: mediaId,
    type: toStringValue(value.type ?? value.media_type ?? value.kind, 'unknown'),
    name: toStringValue(value.name ?? value.file_name ?? value.filename ?? value.title, ''),
    url: mediaUrl || undefined,
    preview_url: previewUrl || undefined,
    preview_video_url: previewVideoUrl || undefined,
    preview_audio_url: previewAudioUrl || undefined,
    thumbnail_url: thumbnailUrl || undefined,
    micro_thumb: microThumb || undefined,
    poster_url: posterUrl || undefined,
    playback_url: playbackUrl || undefined,
    mime_type: toStringValue(value.mime_type ?? value.mimeType ?? value.content_type) || undefined,
    source: toStringValue(value.source ?? value.origin) || undefined,
    size_bytes: toNumberValue(value.size_bytes ?? value.sizeBytes ?? value.size),
    clip_id: toStringValue(value.clip_id ?? value.clipId) || undefined,
    pipeline_run_id: typeof value.pipeline_run_id === 'string' ? value.pipeline_run_id : undefined,
    pipeline_name: typeof value.pipeline_name === 'string' ? value.pipeline_name : undefined,
    is_favourite: typeof value.is_favourite === 'boolean' ? value.is_favourite : false,
    created_at: toStringValue(value.created_at ?? value.createdAt) || undefined,
    prompt: toStringValue(value.prompt) || undefined,
    metadata,
    has_file: typeof value.has_file === 'boolean' ? value.has_file : undefined,
    costCredits: readCostCredits(metadata),
    status: (value.status === 'generating' || value.status === 'failed')
      ? (value.status as 'generating' | 'failed')
      : 'ready',
  };
};

const normalizeMediaLibraryList = (payload: unknown): MediaLibraryItem[] => {
  const rawItems = isRecord(payload)
    ? (Array.isArray(payload.items)
      ? payload.items
      : Array.isArray(payload.media_items)
        ? payload.media_items
        : Array.isArray(payload.media_files)
          ? payload.media_files
          : Array.isArray(payload.data)
            ? payload.data
            : [])
    : Array.isArray(payload)
      ? payload
      : [];

  const deduped = new Map<string, MediaLibraryItem>();
  rawItems
    .map((item, index) => normalizeMediaLibraryItem(item, index))
    .filter((item): item is MediaLibraryItem => Boolean(item))
    .forEach((item) => deduped.set(item.media_id, item));
  return Array.from(deduped.values());
};

const normalizeMediaLibraryUploadResponse = (payload: unknown): MediaLibraryItem | null => {
  if (isRecord(payload)) {
    const candidate = payload.item ?? payload.media_item ?? payload.media ?? payload.data ?? payload;
    return normalizeMediaLibraryItem(candidate, 0);
  }
  return normalizeMediaLibraryItem(payload, 0);
};

const getHttpStatus = (error: unknown): number | undefined =>
  (error as HttpErrorWithStatus | undefined)?.response?.status;

const shouldFallbackToLegacyRoute = (error: unknown): boolean => {
  const status = getHttpStatus(error);
  return status === 404 || status === 405;
};

// ==================== Lip Sync Request Types ====================

export interface LipSyncAudioRequest {
  audio_media_id: string;
}

export interface LipSyncSpeechRequest {
  text: string;
  voice: string;
  speed: number;
  pitch: number;
}

// ==================== API Functions ====================

const getMediaItem = (mediaId: string) =>
  axios.get(`${BASE_URL}/media/${encodeURIComponent(mediaId)}`).then((res) => res.data);

/**
 * Fetch the lineage (parent / siblings / variations) of a media item for the
 * inspector. Normalizes each related entry the same way the list does so the
 * thumbnail / micro-thumb fields resolve. Returns empty lineage on any error so
 * the inspector degrades gracefully (no lineage section) rather than throwing.
 */
const getRelatedMedia = async (mediaId: string): Promise<RelatedMediaResult> => {
  const empty: RelatedMediaResult = { parent: null, siblings: [], variations: [] };
  try {
    const data = await axios
      .get(`${BASE_URL}/media/${mediaId}/related`)
      .then((res) => res.data);
    if (!isRecord(data)) return empty;
    const parentRaw = data.parent;
    const parent = isRecord(parentRaw) ? normalizeMediaLibraryItem(parentRaw, 0) : null;
    const siblings = Array.isArray(data.siblings)
      ? data.siblings
          .map((it, i) => normalizeMediaLibraryItem(it, i))
          .filter((it): it is MediaLibraryItem => Boolean(it))
      : [];
    const variations = Array.isArray(data.variations)
      ? data.variations
          .map((it, i) => normalizeMediaLibraryItem(it, i))
          .filter((it): it is MediaLibraryItem => Boolean(it))
      : [];
    return { parent, siblings, variations };
  } catch {
    return empty;
  }
};

const createMediaItem = (request: NewMediaItemRequest) =>
  axios.post(`${BASE_URL}/media`, request).then((res) => res.data.media_id);

const listMediaLibrary = async (query?: MediaLibraryListQuery): Promise<MediaLibraryItem[]> => {
  const params = {
    search: query?.search?.trim() || undefined,
    type: query?.type?.trim() || undefined,
    source: query?.source?.trim() || undefined,
    role: query?.role?.trim() || undefined,
    favourite: query?.favourite,
    sort: query?.sort,
    created_after: query?.created_after,
    has_controlnet: query?.has_controlnet,
    page: query?.page ?? undefined,
    limit: query?.limit ?? undefined,
    generator_profile_id: query?.generator_profile_id?.trim() || undefined,
  };

  try {
    const payload = await axios
      .get(`${BASE_URL}/media/library`, { params })
      .then((res) => res.data);
    return normalizeMediaLibraryList(payload);
  } catch (error) {
    if (!shouldFallbackToLegacyRoute(error)) {
      throw error;
    }
  }

  const legacyPayload = await axios.get(`${BASE_URL}/media`, { params }).then((res) => res.data);
  return normalizeMediaLibraryList(legacyPayload);
};

const listMediaLibraryPaged = async (query?: MediaLibraryListQuery): Promise<MediaLibraryPage> => {
  const params = {
    search: query?.search?.trim() || undefined,
    type: query?.type?.trim() || undefined,
    source: query?.source?.trim() || undefined,
    role: query?.role?.trim() || undefined,
    sort: query?.sort,
    page: query?.page ?? 1,
    limit: query?.limit ?? 20,
    generator_profile_id: query?.generator_profile_id?.trim() || undefined,
  };

  try {
    const payload = await axios
      .get(`${BASE_URL}/media/library`, { params })
      .then((res) => res.data);
    return {
      items: normalizeMediaLibraryList(payload),
      total: (payload as Record<string, unknown>)?.total as number ?? 0,
      page: (payload as Record<string, unknown>)?.page as number ?? params.page,
      limit: (payload as Record<string, unknown>)?.limit as number ?? params.limit,
    };
  } catch (error) {
    if (!shouldFallbackToLegacyRoute(error)) {
      throw error;
    }
  }

  const legacyPayload = await axios.get(`${BASE_URL}/media`, { params }).then((res) => res.data);
  const items = normalizeMediaLibraryList(legacyPayload);
  return {
    items,
    total: (legacyPayload as Record<string, unknown>)?.total as number ?? items.length,
    page: params.page,
    limit: params.limit,
  };
};

const uploadMediaLibraryFile = async (
  file: File,
  options?: {
    type?: string;
    source?: string;
    /**
     * Whether the backend may spend one small vision call describing the image
     * for the AI reviewer. Default (field omitted) is yes; pass `false` for a
     * bulk drop where most files will never be referenced, and the user can
     * still Describe the ones that are.
     */
    describe?: boolean;
  }
): Promise<MediaLibraryItem> => {
  const body = new FormData();
  body.append('file', file);
  if (options?.type?.trim()) {
    body.append('type', options.type.trim());
  }
  if (options?.source?.trim()) {
    body.append('source', options.source.trim());
  }
  // Only the opt-OUT is sent: an absent field means "describe it", so a caller
  // that says nothing keeps the backend default.
  if (options?.describe === false) {
    body.append('describe', '0');
  }

  let payload: unknown;
  try {
    payload = await axios
      .post(`${BASE_URL}/media/library/upload`, body)
      .then((res) => res.data);
  } catch (error) {
    if (!shouldFallbackToLegacyRoute(error)) {
      throw error;
    }
    payload = await axios.post(`${BASE_URL}/media/upload`, body).then((res) => res.data);
  }

  const normalized = normalizeMediaLibraryUploadResponse(payload);
  if (!normalized) {
    throw new Error('Upload succeeded but no media ID was returned.');
  }
  return normalized;
};

/** Creates a cropped derivative while leaving the source media item untouched. */
const cropMediaImage = async (
  mediaId: string,
  crop: CropImageRequest
): Promise<MediaLibraryItem> => {
  const payload = await axios
    .post(`${BASE_URL}/media/${encodeURIComponent(mediaId)}/crop`, crop)
    .then((res) => res.data);
  const normalized = normalizeMediaLibraryUploadResponse(payload);
  if (!normalized) {
    throw new Error('Crop succeeded but no media ID was returned.');
  }
  return normalized;
};

const renameMediaLibraryItem = async (
  mediaId: string,
  nextName: string
): Promise<MediaLibraryItem> => {
  const trimmedName = nextName.trim();
  if (!trimmedName) {
    throw new Error('Media name is required.');
  }

  const payload: RenameMediaItemRequest = {
    name: trimmedName,
    new_name: trimmedName,
  };

  let responsePayload: unknown;
  try {
    responsePayload = await axios
      .put(`${BASE_URL}/media/library/${mediaId}/rename`, payload)
      .then((res) => res.data);
  } catch (error) {
    if (!shouldFallbackToLegacyRoute(error)) {
      throw error;
    }
    responsePayload = await axios
      .put(`${BASE_URL}/media/${mediaId}/rename`, payload)
      .then((res) => res.data);
  }

  const normalized = normalizeMediaLibraryUploadResponse(responsePayload);
  if (normalized) {
    return {
      ...normalized,
      name: normalized.name || trimmedName,
    };
  }

  return {
    id: mediaId,
    media_id: mediaId,
    type: 'unknown',
    name: trimmedName,
  };
};

const createImage = (
  clipId: string,
  prompt: string,
  metadata?: Record<string, any>,
  outputSpec?: MediaOutputSpec
) =>
  createMediaItem({
    clip_id: clipId,
    type: 'image',
    prompt,
    metadata,
    output_spec: outputSpec,
  });

const createAIVideo = (
  clipId: string,
  prompt: string,
  metadata?: Record<string, any>,
  outputSpec?: MediaOutputSpec
) =>
  createMediaItem({
    clip_id: clipId,
    type: 'ai_video',
    prompt,
    metadata,
    output_spec: outputSpec,
  });

const createAudio = (
  clipId: string,
  prompt: string,
  metadata?: Record<string, any>,
  outputSpec?: MediaOutputSpec
) =>
  createMediaItem({
    clip_id: clipId,
    type: 'audio',
    prompt,
    metadata,
    output_spec: outputSpec,
  });

const editMediaItem = (mediaId: string, request: EditMediaItemRequest) =>
  axios.put(`${BASE_URL}/media/${mediaId}`, request).then((res) => res.data);

const regenerateMedia = (mediaId: string, request?: RegenerateMediaRequest) =>
  axios
    .post(`${BASE_URL}/media/${mediaId}/regenerate`, request ?? {})
    .then((res) => res.data);

const editMediaMetadata = (mediaId: string, key: string, value: any) =>
  axios.put(`${BASE_URL}/media/${mediaId}/metadata`, { key, value }).then((res) => res.data);

/**
 * Asks a vision model to describe a media item (used by the reviewer trace
 * panel to caption a candidate frame). Cached on the media item after the
 * first call — pass `force` to bypass the cache and re-describe.
 */
const describeMedia = (
  mediaId: string,
  force?: boolean
): Promise<{ description: string; model: string; cached: boolean }> =>
  axios
    .post(`${BASE_URL}/media/${mediaId}/describe${force ? '?force=1' : ''}`)
    .then((res) => res.data);

const replaceMediaMetadata = (mediaId: string, metadata: Record<string, any>) =>
  axios.put(`${BASE_URL}/media/${mediaId}/metadata/replace`, { metadata }).then((res) => res.data);

const deleteMediaItem = (mediaId: string) =>
  axios.delete(`${BASE_URL}/media/${mediaId}`).then((res) => res.data);

const toggleFavourite = (mediaId: string, isFavourite: boolean) =>
  axios
    .patch(`${BASE_URL}/media/${mediaId}/favourite`, { is_favourite: isFavourite })
    .then((res) => res.data);

const getMediaStats = async (): Promise<MediaStats> => {
  try {
    const payload = await axios
      .get(`${BASE_URL}/media/stats`)
      .then((res) => res.data);
    const p = payload as Record<string, unknown>;
    return {
      total: (typeof p.total === 'number' ? p.total : 0),
      image: (typeof p.image === 'number' ? p.image : 0),
      video: (typeof p.video === 'number' ? p.video : 0),
      audio: (typeof p.audio === 'number' ? p.audio : 0),
    };
  } catch {
    return { total: 0, image: 0, video: 0, audio: 0 };
  }
};

const renameMediaItem = async (id: string, name: string): Promise<MediaLibraryItem> => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error('Media name is required.');
  }
  let responsePayload: unknown;
  try {
    responsePayload = await axios
      .put(`${BASE_URL}/media/${id}/rename`, { name: trimmedName, new_name: trimmedName })
      .then((res) => res.data);
  } catch (error) {
    if (!shouldFallbackToLegacyRoute(error)) {
      throw error;
    }
    responsePayload = await axios
      .put(`${BASE_URL}/media/library/${id}/rename`, { name: trimmedName, new_name: trimmedName })
      .then((res) => res.data);
  }
  const normalized = normalizeMediaLibraryUploadResponse(responsePayload);
  if (normalized) {
    return { ...normalized, name: normalized.name || trimmedName };
  }
  return { id, media_id: id, type: 'unknown', name: trimmedName };
};

const lipSyncMedia = (
  clipId: string,
  mediaId: string,
  request: LipSyncAudioRequest | LipSyncSpeechRequest
) =>
  axios
    .post(`${BASE_URL}/clips/${clipId}/media/${mediaId}/lipsync`, request)
    .then((res) => res.data);

// ==================== Export ====================

const MediaAPI = {
  getMediaItem,
  getRelatedMedia,
  getMediaStats,
  createMediaItem,
  listMediaLibrary,
  listMediaLibraryPaged,
  uploadMediaLibraryFile,
  cropMediaImage,
  renameMediaItem,
  renameMediaLibraryItem,
  createImage,
  createAIVideo,
  createAudio,
  editMediaItem,
  regenerateMedia,
  editMediaMetadata,
  describeMedia,
  replaceMediaMetadata,
  deleteMediaItem,
  toggleFavourite,
  lipSyncMedia,
};

export default MediaAPI;
